import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useReferenceData } from '../../contexts/ReferenceDataContext';
import Loader from '../../components/common/Loader';
import { can, PERMISSIONS, getPeopleBasePath } from '../../utils/permissions';
import '../../styles/Forms.css';

// 👇 defines which roles are allowed to be the manager of a given role
// Hierarchy: Admin > HOD > Manager > Team Leader > Employee
const ALLOWED_MANAGER_ROLES = {
  employee: ['admin', 'hod', 'manager', 'team-leader'],
  'team-leader': ['admin', 'hod', 'manager'],
  manager: ['admin', 'hod'],
  hod: ['admin'],
  admin: [], // admin has no manager
};

// 👇 union of every role that can ever be a manager (used to fetch only
//    manager-eligible users, instead of the full paginated user list)
const MANAGER_ELIGIBLE_ROLES = [
  ...new Set(Object.values(ALLOWED_MANAGER_ROLES).flat()),
]; // ['admin', 'hod', 'manager', 'team-leader']

const AddPersonPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const peopleBase = getPeopleBasePath(location.pathname);
  const { id } = useParams();
  const isEdit = !!id;
  const sessionUser = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  }, []);
  const isSelfEdit =
    isEdit && String(sessionUser?.id || sessionUser?._id) === String(id);
  const canUseEmployeeForm =
    !isEdit ? can(PERMISSIONS.ADD_EMPLOYEE) : can(PERMISSIONS.EDIT_EMPLOYEE) || isSelfEdit;
  const { jobTypeRows } = useReferenceData();
  const jobTypes = useMemo(() => {
    return jobTypeRows
      .map((item) => {
        if (typeof item === 'string') return item;
        return item?.name || item?.jobType || item?.title || '';
      })
      .filter(Boolean);
  }, [jobTypeRows]);

  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]); // 👈 for Manager dropdown
  const [showPasswords, setShowPasswords] = useState({
    password: false,
    confirmPassword: false
  });

  const [formData, setFormData] = useState({
    name: '',
    employeeId: '',
    email: '',
    phone: '',
    title: '',
    site: '',
    department: '',
    location: '',
    manager: '', // 👈 new field
    notes: '',
    password: '',
    confirmPassword: '',
    role: 'employee',
    jobType: '',
  });

  // 👇 filter employees list to only allowed manager roles for the currently selected role
  const availableManagers = useMemo(() => {
    const allowedRoles = ALLOWED_MANAGER_ROLES[formData.role] || [];
    return employees.filter(
      (emp) =>
        allowedRoles.includes(emp.role) &&
        String(emp.id || emp._id) !== String(id) // a person can't be their own manager
    );
  }, [employees, formData.role, id]);

  useEffect(() => {
    if (!canUseEmployeeForm) {
      toast.error('You do not have permission to manage employees');
      navigate(peopleBase);
    }
  }, [canUseEmployeeForm, navigate, peopleBase]);

  useEffect(() => {
    fetchSitesAndDepartments();
  }, []);

  // Load existing user when editing
  useEffect(() => {
    const fetchUser = async () => {
      if (!isEdit) return;

      try {
        setLoading(true);
        const response = await api.getUser(id);
        if (response.success && response.data) {
          const user = response.data;
          setFormData(prev => ({
            ...prev,
            name: user.name || '',
            employeeId: user.employeeId || '',
            email: user.email || '',
            phone: user.phone || '',
            title: user.title || '',
            site: user.site?._id || user.site || '',
            department: user.department?._id || user.department || '',
            location: user.location || '',
            manager: user.manager?._id || user.manager || '', // 👈 new field
            notes: user.notes || '',
            role: user.role || 'employee',
            jobType: user.jobType || '',
            password: '',
            confirmPassword: '',
          }));
        }
        console.log(response.data);
      } catch (error) {
        console.error('Error loading employee:', error);
        toast.error('Error loading employee details');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id, isEdit]);

  const fetchSitesAndDepartments = async () => {
    try {
      const sitesRes = await api.getSites({ isActive: 'true' });
      if (sitesRes.success) {
        setSites(sitesRes.data || []);
      }

      const deptsRes = await api.getDepartments({ isActive: 'true' });
      if (deptsRes.success) {
        setDepartments(deptsRes.data || []);
      }

      const locationsRes = await api.getLocations({ isActive: 'true' });
      if (locationsRes.success) {
        setLocations(locationsRes.data || []);
      }

      // 👇 fetch only manager-eligible users for the Manager dropdown.
      //    We pass every role that can be a manager (comma-separated) plus a
      //    high limit, because the API otherwise defaults to 25 per page
      //    sorted newest-first and silently drops older users (e.g. an older
      //    admin) from the options.
      const usersRes = await api.getUsers({
        role: MANAGER_ELIGIBLE_ROLES.join(','),
        limit: 1000,
      });
      if (usersRes.success) {
        setEmployees(usersRes.data || []);
      }
    } catch (error) {
      console.error('Error fetching sites and departments:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // If role changes, clear manager selection since allowed managers differ per role
    if (name === 'role') {
      setFormData(prev => ({
        ...prev,
        role: value,
        manager: '',
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.email) {
      toast.error('Please fill in required fields (Name and Email)');
      return;
    }

    if (formData.password && formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password && formData.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    try {
      setLoading(true);

      const userData = {
        name: formData.name,
        email: formData.email,
        role: formData.role || 'employee',
        jobType: formData.jobType || undefined,
        employeeId: formData.employeeId || undefined,
        phone: formData.phone || undefined,
        title: formData.title || undefined,
        site: formData.site || undefined,
        department: formData.department || undefined,
        location: formData.location || undefined,
        manager: formData.role === 'admin' ? undefined : (formData.manager || undefined), // 👈 admin never has a manager
        notes: formData.notes || undefined,
      };

      // For new users, password is required (used for hashing, but reset email will be sent)
      if (!isEdit) {
        // Use a temporary password for creation - user will reset via email
        userData.password = formData.password || 'TempPassword@123';
      } else if (formData.password) {
        // For editing, only update password if provided
        userData.password = formData.password;
      }

      let response;
      if (isEdit) {
        response = await api.updateUser(id, userData);
      } else {
        response = await api.createUser(userData);
      }

      if (response.success) {
        if (isEdit) {
          // Check if password was changed (backend will include message if password was changed)
          const message = response.message || 'Employee updated successfully!';
          toast.success(message, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
          });
        } else {
          toast.success(
            'Employee created successfully! 📧 A welcome email with login credentials has been sent to ' + formData.email,
            {
              position: 'top-right',
              autoClose: 5000,
              hideProgressBar: false,
            }
          );
        }
        setTimeout(() => {
          navigate(peopleBase);
        }, 1000);
      } else {
        toast.error(
          (isEdit ? 'Error updating employee: ' : 'Error adding employee: ') +
          (response.message || 'Unknown error')
        );
      }
    } catch (error) {
      console.error(isEdit ? 'Error updating employee:' : 'Error adding employee:', error);
      toast.error(
        (isEdit ? 'Error updating employee: ' : 'Error adding employee: ') +
        (error.message || 'Unknown error')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-person-page" style={{ padding: '16px' }}>
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
          {/* Left Column - Basic Information */}
          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Basic Information</h2>

            <div className="form-group">
              <label htmlFor="name">Full Name *</label>
              <input
                id="name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter full name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="employeeId">Employee ID</label>
              <input
                id="employeeId"
                type="text"
                name="employeeId"
                value={formData.employeeId}
                onChange={handleInputChange}
                placeholder="Enter employee ID"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter email address"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input
                id="phone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="Enter phone number"
              />
            </div>

            <div className="form-group">
              <label htmlFor="title">Title / Position</label>
              <input
                id="title"
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter job title"
              />
            </div>

            <div className="form-group">
              <label htmlFor="role">Role</label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleInputChange}
              >
                <option value="admin">Admin</option>
                <option value="hod">HOD</option>
                <option value="manager">Manager</option>
                <option value="team-leader">Team Leader</option>
                <option value="employee">Employee</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="jobType">Job Type</label>
              <select
                id="jobType"
                name="jobType"
                value={formData.jobType}
                onChange={handleInputChange}
              >
                <option value="">Select Job Type</option>
                {[...new Set([...(jobTypes || []), formData.jobType].filter(Boolean))].map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {/* 👇 Manager field - placed after Job Type; options depend on selected Role */}
            {formData.role !== 'admin' && (
              <div className="form-group">
                <label htmlFor="manager">Manager</label>
                <select
                  id="manager"
                  name="manager"
                  value={formData.manager}
                  onChange={handleInputChange}
                >
                  <option value="">Select Manager</option>
                  {availableManagers.map(emp => (
                    <option key={emp.id || emp._id} value={emp.id || emp._id}>
                      {emp.name} {emp.title ? `(${emp.title})` : ''}
                    </option>
                  ))}
                </select>
                {availableManagers.length === 0 && (
                  <small style={{ color: '#718096', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    No eligible managers found for this role yet.
                  </small>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Location & Additional Info */}
          <div>
            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              marginBottom: '16px'
            }}>
              <h2 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Location & Department</h2>

              <div className="form-group">
                <label htmlFor="site">Site</label>
                <select
                  id="site"
                  name="site"
                  value={formData.site}
                  onChange={handleInputChange}
                >
                  <option value="">Select Site</option>
                  {sites.map(site => (
                    <option key={site.id || site._id} value={site.id || site._id}>
                      {site.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="department">Department</label>
                <select
                  id="department"
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                >
                  <option value="">Select Department</option>
                  {departments.map(dept => (
                    <option key={dept.id || dept._id} value={dept.id || dept._id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="location">Location</label>
                <select
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                >
                  <option value="">Select Location</option>
                  {locations.map(loc => (
                    <option key={loc.id || loc._id} value={loc.name}>
                      {loc.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              marginBottom: '16px'
            }}>
              <h2 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Account Settings</h2>

              <div className="form-group">
                <label htmlFor="password">Password {!isEdit && '(Create user only)'}</label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="password"
                    type={showPasswords.password ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder={isEdit ? "Leave empty to keep current password" : "Leave empty for automatic password"}
                    style={{ paddingRight: '40px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, password: !prev.password }))}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#718096',
                      transition: 'color 0.3s ease'
                    }}
                    aria-label={showPasswords.password ? 'Hide password' : 'Show password'}
                  >
                    {showPasswords.password ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    )}
                  </button>
                </div>
                <small style={{ color: '#718096', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                  {isEdit ? 'An email with the new password will be sent to the user\'s email' : 'A welcome email with login credentials will be sent to the user\'s email'}
                </small>
              </div>

              {formData.password && (
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="confirmPassword"
                      type={showPasswords.confirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="Confirm password"
                      style={{ paddingRight: '40px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, confirmPassword: !prev.confirmPassword }))}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#718096',
                        transition: 'color 0.3s ease'
                      }}
                      aria-label={showPasswords.confirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPasswords.confirmPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                          <line x1="1" y1="1" x2="23" y2="23"></line>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <h2 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Additional Notes</h2>

              <div className="form-group">
                <label htmlFor="notes">Notes</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Enter any additional notes about this employee"
                  rows="4"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div style={{
          display: 'flex',
          gap: '10px',
          justifyContent: 'flex-end',
          paddingTop: '16px',
          borderTop: '1px solid #e2e8f0'
        }}>
          <button
            type="button"
            onClick={() => navigate(peopleBase)}
            style={{
              padding: '10px 20px',
              background: '#e2e8f0',
              color: '#4a5568',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '13px'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '13px',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? (isEdit ? 'Updating...' : 'Creating...') : isEdit ? 'Update Employee' : 'Create Employee'}
          </button>
        </div>
      </form>

      {loading && <Loader />}
    </div>
  );
};

export default AddPersonPage;